import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'clear', 'category', 'clearFilters']

  static values = {
    delay: { type: Number, default: 350 },
    frame: { type: String, default: 'workspace_products_catalog' }
  }

  connect () {
    this.timer = null
    this.requestId = 0
    this.lastSubmittedQuery = this.inputTarget.value.trim()
    this.pendingRequestId = null
    this.onFrameRender = this.onFrameRender.bind(this)
    document.addEventListener('turbo:frame-render', this.onFrameRender)
    this.syncTabFromUrl()
    this.syncClear()
    this.syncFiltersVisibility()
  }

  disconnect () {
    document.removeEventListener('turbo:frame-render', this.onFrameRender)
    if (this.timer) clearTimeout(this.timer)
  }

  queueSubmit () {
    this.syncClear()
    this.syncFiltersVisibility()
    if (this.timer) clearTimeout(this.timer)
    this.timer = window.setTimeout(() => this.submitIfChanged(), this.delayValue)
  }

  submitIfChanged () {
    const query = this.inputTarget.value.trim()
    if (query === this.lastSubmittedQuery) return
    this.submitSearch(query)
  }

  submit () {
    if (this.timer) clearTimeout(this.timer)
    this.submitSearch(this.inputTarget.value.trim())
  }

  submitSearch (query) {
    this.lastSubmittedQuery = query
    this.syncTabFromUrl()
    this.requestId += 1
    this.pendingRequestId = this.requestId
    this.element.requestSubmit()
    this.syncFiltersVisibility()
  }

  onFrameRender (event) {
    if (event.target.id !== this.frameValue) return

    const finishedRequestId = this.pendingRequestId
    this.pendingRequestId = null

    this.syncTabFromUrl()
    this.syncCategoryOptions()
    this.syncFiltersVisibility()

    if (finishedRequestId !== this.requestId) return
    if (this.inputTarget.value.trim() !== this.lastSubmittedQuery) return
    if (document.activeElement === this.inputTarget) return

    this.inputTarget.focus({ preventScroll: true })
    const end = this.inputTarget.value.length
    this.inputTarget.setSelectionRange(end, end)
  }

  syncCategoryOptions () {
    if (!this.hasCategoryTarget) return

    const meta = document.getElementById('workspace-product-filter-meta')
    if (!meta) return

    const total = meta.dataset.categoryTotal || '0'
    let counts = {}
    try {
      counts = JSON.parse(meta.dataset.categoryCounts || '{}')
    } catch (_error) {
      counts = {}
    }

    const select = this.categoryTarget
    if (select.options.length === 0) return

    select.options[0].text = `All categories (${total})`
    Array.from(select.options).slice(1).forEach((option) => {
      const baseLabel = option.text.replace(/\s\(\d+\)$/, '')
      option.text = `${baseLabel} (${counts[option.value] || 0})`
    })
  }

  syncTabFromUrl () {
    const tab = new URL(window.location.href).searchParams.get('tab')
    const existing = this.element.querySelector('input[name="tab"]')

    if (tab && tab !== 'all') {
      if (existing) {
        existing.value = tab
      } else {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'tab'
        input.value = tab
        this.element.prepend(input)
      }
    } else if (existing) {
      existing.remove()
    }
  }

  syncClear () {
    if (!this.hasClearTarget) return
    this.clearTarget.classList.toggle('hidden', !this.inputTarget.value.trim())
  }

  syncFiltersVisibility () {
    if (!this.hasClearFiltersTarget) return

    const hasSearch = this.inputTarget.value.trim().length > 0
    const hasCategory = this.hasCategoryTarget && this.categoryTarget.value !== ''
    const tab = new URL(window.location.href).searchParams.get('tab')
    const hasTab = tab && tab !== 'all'

    this.clearFiltersTarget.classList.toggle('hidden', !(hasSearch || hasCategory || hasTab))
  }

  clear (event) {
    event.preventDefault()
    if (this.timer) clearTimeout(this.timer)
    this.inputTarget.value = ''
    this.lastSubmittedQuery = ''
    this.syncClear()
    this.syncTabFromUrl()
    this.requestId += 1
    this.pendingRequestId = this.requestId
    this.element.requestSubmit()
    this.syncFiltersVisibility()
    this.inputTarget.focus({ preventScroll: true })
  }

  clearAll (event) {
    event.preventDefault()
    if (this.timer) clearTimeout(this.timer)

    this.inputTarget.value = ''
    this.lastSubmittedQuery = ''
    if (this.hasCategoryTarget) this.categoryTarget.value = ''
    this.element.querySelector('input[name="tab"]')?.remove()

    this.syncClear()
    this.syncFiltersVisibility()
    this.requestId += 1
    this.pendingRequestId = this.requestId
    this.element.requestSubmit()
  }
}
